import type {
    AnyLayer,
    GeoJSONSource,
    LngLatLike,
    Map as MapBoxMap,
    MapLayerMouseEvent,
    SymbolLayout,
} from 'mapbox-gl';

import { debug, type DebugHelper } from '@gebruederheitz/debuggable';
import {
    type ApiState,
    UserStatus,
    GameApi,
    type GamePOI,
    GamePOIType,
} from 'urban-challenger-sdk';

import popupTemplate from './popup-template';

enum TaskStatus {
    Initial = 'initial',
    Pending = 'pending',
    Rejected = 'rejected',
    Approved = 'approved',
}

export class GameMap {
    protected readonly debug: DebugHelper;
    protected sdk?: GameApi;
    protected mapboxgl?: typeof import('mapbox-gl');
    protected map: MapBoxMap;
    protected challenges: Map<string, GeoJSON.Feature> = new Map();
    protected layers: Map<string, AnyLayer> = new Map();
    protected sources: Map<string, GeoJSONSource> = new Map();

    constructor(debugBase: DebugHelper = debug) {
        this.debug = debugBase.spawn(this, true);
    }

    public async useExistingMap(): Promise<this> {
        if (window.ucMapboxMap && window.mapboxgl) {
            this.debug.log('using existing map, which is', window.ucMapboxMap);
            this.mapboxgl = window.mapboxgl;
            this.map = window.ucMapboxMap;

            if (!this.map.loaded()) {
                await new Promise((res) => {
                    this.map.on('load', res);
                });
            }
        } else {
            this.map = await new Promise<MapBoxMap>((resolve, reject) => {
                let attempts = 0;
                const maxAttempts = 10;
                const interval = setInterval(() => {
                    if (window.map && 'addLayer' in window.ucMapboxMap) {
                        clearInterval(interval);
                        this.mapboxgl = window.mapboxgl;
                        resolve(window.ucMapboxMap);
                    } else {
                        ++attempts;
                        if (attempts >= maxAttempts) {
                            clearInterval(interval);
                            reject(new Error('Map not found'));
                        }
                    }
                }, 1000);
            });
        }

        return this;
    }

    public async initCustomMap(): Promise<this> {
        const mapboxgl = await import('mapbox-gl');
        this.mapboxgl = mapboxgl;
        this.mapboxgl.accessToken = process.env.ACCESS_TOKEN;
        this.map = new mapboxgl.Map({
            container: 'mapbox-container',
            // style: 'mapbox://styles/urbanchallenger/clfgofryc00js01mtz9jvjps2',
            // andi dev copy:
            style: 'mapbox://styles/urbanchallenger/cm9jxmomn007501s554kq4cvz',
            center: [8.47, 49.488],
            zoom: 14.6,
        });

        this.adjustZoom();
        // Add zoom and rotation controls to the map.
        this.map.addControl(new this.mapboxgl.NavigationControl());
        this.addUserLocationMarker();
        this.resizeMapOnTabChange();

        return this;
    }

    public setChallenges(challenges: Map<string, GeoJSON.Feature>): this {
        this.challenges = challenges;
        this.layers = new Map<string, AnyLayer>();
        this.sources = new Map<string, GeoJSONSource>();

        this.map.on('load', this.addChallengeLayers);

        return this;
    }

    public setState(sdk: GameApi): this {
        this.sdk = sdk;

        return this;
    }

    public autoUpdateChallenges(): this {
        if (!this.sdk) {
            console.warn('No state, SDK needs to be set first');
            return this;
        }

        for (const challengeSlug of this.challenges.keys()) {
            const challengeState = this.sdk.getDerivedState<
                null | 'pending' | 'approved' | 'rejected'
            >(({ status: userStatus, team }: ApiState) => {
                if (userStatus < UserStatus.PLAYING) {
                    return null;
                }
                if (team?.tasks[challengeSlug]) {
                    const cStatus = team.tasks[challengeSlug];
                    // This is where we could also attach information about
                    // comments, should they exist, or points gained
                    return cStatus.state;
                }

                return null;
            });

            this.debug.devnull(challengeState);
        }

        return this;
    }

    public async addStartFinish(
        startPoi?: GamePOI,
        finishPoi?: GamePOI
    ): Promise<void> {
        this.debug.log('add start finish', { startPoi, finishPoi });

        if (startPoi) {
            const features = [this.mapPoiToGeoJsonFeature(startPoi)];
            const sourceId = 'event-start';

            /*
            @NB: workaround while we don't _always_ load mapbox ourselves
             */
            // this.map.addSource(sourceId, this.makeGeoJSONSource(features));
            this.map.addSource(sourceId, {
                type: 'geojson',
                data: this.makeGeoJSONFeatureCollection(features),
            });

            const startLayer: AnyLayer = await this.getSymbolLayer(
                sourceId,
                sourceId,
                this.sdk.getBaseUrl() + '/img/start-32.png',
                '#188ddb'
            );

            this.map.addLayer(startLayer);
            this.attachMarkerEventsToLayer(startLayer);
        }

        if (finishPoi) {
            const features = [this.mapPoiToGeoJsonFeature(finishPoi)];
            const sourceId = 'event-finish';

            /*
            @NB: workaround while we don't _always_ load mapbox ourselves
             */
            // this.map.addSource(sourceId, this.makeGeoJSONSource(features));
            this.map.addSource(sourceId, {
                type: 'geojson',
                data: this.makeGeoJSONFeatureCollection(features),
            });

            const finishLayer: AnyLayer = await this.getSymbolLayer(
                sourceId,
                sourceId,
                this.sdk.getBaseUrl() + '/img/finish-32.png',
                '#188ddb'
            );

            this.map.addLayer(finishLayer);
            this.attachMarkerEventsToLayer(finishLayer);
        }
    }

    public async addGenericPoi(gamePoi: GamePOI[]): Promise<void> {
        if (gamePoi.length) {
            const features = gamePoi.map(this.mapPoiToGeoJsonFeature);

            if (features.length) {
                const id = 'event-poi';

                this.map.addSource(id, {
                    type: 'geojson',
                    data: this.makeGeoJSONFeatureCollection(features),
                });

                const layer = await this.getSymbolLayer(
                    id,
                    id,
                    this.sdk.getBaseUrl() + '/img/poi.png',
                    '#188ddb'
                );

                this.map.addLayer(layer);
                this.attachMarkerEventsToLayer(layer);
            }
        }
    }

    protected loadCustomImage(url: string, alias: string): Promise<boolean> {
        return new Promise<boolean>((res) => {
            this.map.loadImage(url, (err, img) => {
                if (err) {
                    this.debug.error(err);
                    res(false);
                } else {
                    this.map.addImage(alias, img);
                    res(true);
                }
            });
        });
    }

    // protected onUpdate = () => {
    //     for (const [status, layer] of this.layers.entries()) {
    //         const id = this.getIdByStatus(status as TaskStatus);
    //         const source = this.map.getSource(id);
    //
    //         if (source.type === 'geojson') {
    //             const previousData = source.data;
    //             source.setData(this.makeGeoJSONFeatureCollection(Array.from()));
    //         }
    //     }
    // };

    protected addChallengeLayers = (): void => {
        Object.values(TaskStatus).forEach((status) => {
            const sourceId = this.getIdByStatus(status);
            let dataPoints = [];

            if (status === TaskStatus.Initial.valueOf()) {
                dataPoints = Array.from(this.challenges.values());
            }

            this.map.addSource(sourceId, this.makeGeoJSONSource(dataPoints));
            const layer = this.getMarkerLayer(
                sourceId,
                sourceId,
                this.getColorByStatus(status)
            );
            this.layers.set(status, layer);
            this.map.addLayer(layer);
            this.attachMarkerEventsToLayer(layer);
        });
    };

    protected attachMarkerEventsToLayer(layer: AnyLayer): void {
        this.map.on('click', layer.id, (e: MapLayerMouseEvent) => {
            const coordinates = this.getFeatureCoordinatesFromEvent(e);
            const {
                description = null,
                title = null,
                mapUrl = null,
            } = e.features[0].properties;

            if (description) {
                this.createPopup(coordinates, description);
            } else if (title) {
                this.createPopup(
                    coordinates,
                    popupTemplate({
                        title,
                        mapUrl,
                    })
                );
            }
        });

        // Center the map on the coordinates of any clicked circle from the 'locations' layer.
        this.map.on('click', layer.id, (e: MapLayerMouseEvent) => {
            const point = e.features[0].geometry as GeoJSON.Point;
            this.map.flyTo({
                center: point.coordinates as LngLatLike,
                speed: 0.5,
                curve: 1,
                easing(t) {
                    return t;
                },
            });
        });

        // Change the cursor to a pointer when the mouse is over the 'locations' layer.
        this.map.on('mouseenter', layer.id, () => {
            this.map.getCanvas().style.cursor = 'pointer';
        });

        // Change it back to a pointer when it leaves.
        this.map.on('mouseleave', layer.id, () => {
            this.map.getCanvas().style.cursor = '';
        });
    }

    protected mapPoiToGeoJsonFeature(poi: GamePOI): GeoJSON.Feature {
        let title: string = poi.alias ?? '';

        if (!poi.alias) {
            if (poi.type === GamePOIType.Start) {
                title = 'Start';
            } else if (poi.type === GamePOIType.Finish) {
                title = 'Finish';
            } else {
                title = '';
            }
        }
        return {
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [
                    /* @NB: Long/Lat, not Lat/Long! Tsss... */
                    parseFloat(poi.coordinates.long),
                    parseFloat(poi.coordinates.lat),
                ],
            },
            properties: {
                id: 'event-start',
                title,
                mapUrl: poi.mapUrl ?? '',
                label: poi.label ?? '',
            },
        };
    }

    protected createPopup(coordinates: LngLatLike, content: string): void {
        new window.mapboxgl.Popup()
            .setLngLat(coordinates)
            .setHTML(content)
            .addTo(this.map);
    }

    protected getFeatureCoordinatesFromEvent(
        e: MapLayerMouseEvent
    ): LngLatLike {
        const geometry = e.features[0].geometry as GeoJSON.Point;
        const coordinates = [...geometry.coordinates] as LngLatLike;

        // Ensure that if the map is zoomed out such that multiple
        // copies of the feature are visible, the popup appears
        // over the copy being pointed to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        return coordinates;
    }

    protected addUserLocationMarker(): void {
        this.map.addControl(
            new window.mapboxgl.GeolocateControl({
                positionOptions: {
                    enableHighAccuracy: true,
                },
                // When active the map will receive updates to the device's location as it changes.
                trackUserLocation: true,
                // Draw an arrow next to the location dot to indicate which direction the device is heading.
                showUserHeading: true,
            })
        );
    }

    protected adjustZoom(): void {
        // this.map.setZoom(14.6);
        // Adjust zoom of map for mobile and desktop
        // const mq = window.matchMedia('(min-width: 480px)');
        // if (mq.matches) {
        //     this.map.setZoom(14.6); //set map zoom level for desktop size
        // } else {
        //     this.map.setZoom(14.6); //set map zoom level for mobile size
        // }
    }

    protected resizeMapOnTabChange(): void {
        window.Webflow = window.Webflow || [];
        window.Webflow.push(() => {
            const tabButton = document.getElementById('w-tabs-0-data-w-tab-0');
            tabButton.addEventListener('click', () => {
                if (this.map) {
                    setTimeout(() => {
                        this.map.resize();
                    }, 200);
                }
            });
        });
    }

    protected getMarkerLayer(
        id: string,
        source: string,
        color: string = '#fff'
    ): AnyLayer {
        return {
            id,
            type: 'circle',
            source: source,
            paint: {
                'circle-radius': 12,
                'circle-stroke-width': 2,
                'circle-color': color,
                'circle-opacity': 1,
                'circle-stroke-color': 'white',
            },
        };
    }

    protected async getSymbolLayer(
        id: string,
        source: string,
        imageUrl: string,
        fallbackColor: string = '#fff'
    ): Promise<AnyLayer> {
        const hasCustomImage = await this.loadCustomImage(imageUrl, id);

        if (hasCustomImage) {
            let layout: SymbolLayout = {
                'icon-image': id,
                'icon-size': 1,
                'icon-anchor': 'center',
                'text-field': ['get', 'label'],
                'text-offset': [0, 1],
                'text-anchor': 'top',
            };

            return {
                id,
                type: 'symbol',
                source: source,
                layout,
                paint: {
                    'text-color': '#fff',
                },
            };
        } else {
            return this.getMarkerLayer(id, source, fallbackColor);
        }
    }

    protected makeGeoJSONSource(features: GeoJSON.Feature[]): GeoJSONSource {
        return new this.mapboxgl.GeoJSONSource({
            data: this.makeGeoJSONFeatureCollection(features),
        });
    }

    protected makeGeoJSONFeatureCollection(
        features: GeoJSON.Feature[]
    ): GeoJSON.FeatureCollection<GeoJSON.Geometry> {
        return {
            type: 'FeatureCollection',
            features,
        };
    }

    protected getIdByStatus(status: TaskStatus): string {
        return `markers-${status.valueOf()}`;
    }

    protected getColorByStatus(status: TaskStatus): string {
        switch (status) {
            case TaskStatus.Initial:
                return '#fff';
            case TaskStatus.Pending:
                return '#fcac32';
            case TaskStatus.Rejected:
                return '#c31400';
            case TaskStatus.Approved:
                return '#5cb860';
        }
    }
}
